import { cloneElement, useCallback, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactElement, type ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { Floating, useDismiss, type Placement } from './floating';

export type MenuEntry =
  | {
      type?: 'item';
      id?: string;
      label: string;
      icon?: ReactNode;
      shortcut?: string;
      onSelect?: () => void;
      href?: string;
      danger?: boolean;
      disabled?: boolean;
      /** Marks the currently chosen option in single-choice menus (theme, language). */
      checked?: boolean;
    }
  | { type: 'separator' }
  | { type: 'label'; label: string };

export interface MenuProps {
  trigger: ReactElement<Record<string, unknown>>;
  items: MenuEntry[];
  placement?: Placement;
  /** Accessible name for the menu. */
  label: string;
  className?: string;
  /** Optional header rendered above the items (e.g. signed-in user). */
  header?: ReactNode;
}

/** Dropdown action menu with roving focus, typeahead and full keyboard support (WAI-ARIA menu button). */
export function Menu({ trigger, items, placement = 'bottom-end', label, className, header }: MenuProps) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLElement | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const id = useId();
  const typeahead = useRef({ text: '', timer: 0 });

  const close = useCallback((restoreFocus = true) => {
    setOpen(false);
    if (restoreFocus) {
      anchorRef.current?.focus();
    }
  }, []);
  const refs = useMemo(() => [anchorRef, menuRef], []);
  useDismiss(open, refs, close);

  const focusables = (): HTMLElement[] => Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not([aria-disabled="true"]),[role="menuitemradio"]:not([aria-disabled="true"])') ?? []);

  // Focus the checked (or first) item as soon as the menu element exists.
  const setMenuNode = useCallback((node: HTMLDivElement | null) => {
    const previous = menuRef.current;
    menuRef.current = node;
    if (node && !previous) {
      const list = Array.from(node.querySelectorAll<HTMLElement>('[role="menuitem"]:not([aria-disabled="true"]),[role="menuitemradio"]:not([aria-disabled="true"])'));
      (list.find((el) => el.getAttribute('aria-checked') === 'true') ?? list[0])?.focus();
    }
  }, []);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const list = focusables();
    const index = list.indexOf(document.activeElement as HTMLElement);
    const move = (next: number): void => {
      event.preventDefault();
      list[(next + list.length) % list.length]?.focus();
    };
    switch (event.key) {
      case 'ArrowDown':
        move(index + 1);
        break;
      case 'ArrowUp':
        move(index - 1);
        break;
      case 'Home':
        move(0);
        break;
      case 'End':
        move(list.length - 1);
        break;
      case 'Tab':
        close(false);
        break;
      default:
        if (event.key.length === 1 && !event.metaKey && !event.ctrlKey) {
          window.clearTimeout(typeahead.current.timer);
          typeahead.current.text += event.key.toLowerCase();
          typeahead.current.timer = window.setTimeout(() => (typeahead.current.text = ''), 600);
          const match = list.find((el) => el.textContent?.trim().toLowerCase().startsWith(typeahead.current.text));
          match?.focus();
        }
    }
  };

  const originalOnClick = trigger.props.onClick as ((e: unknown) => void) | undefined;
  const originalKeyDown = trigger.props.onKeyDown as ((e: KeyboardEvent<HTMLElement>) => void) | undefined;
  const element = cloneElement(trigger, {
    ref: (node: HTMLElement | null) => {
      anchorRef.current = node;
    },
    'aria-haspopup': 'menu',
    'aria-expanded': open,
    'aria-controls': open ? id : undefined,
    onClick: (event: unknown) => {
      originalOnClick?.(event);
      setOpen((value) => !value);
    },
    onKeyDown: (event: KeyboardEvent<HTMLElement>) => {
      originalKeyDown?.(event);
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        setOpen(true);
      }
    },
  });

  const run = (entry: Extract<MenuEntry, { label: string; type?: 'item' }>): void => {
    close(true);
    entry.onSelect?.();
  };

  return (
    <>
      {element}
      {open && (
        <Floating
            anchorRef={anchorRef}
            ref={setMenuNode}
            id={id}
            role="menu"
            aria-label={label}
            tabIndex={-1}
            onKeyDown={onKeyDown}
            placement={placement}
            className={cn('glass-strong min-w-[13rem] max-w-[calc(100vw-1rem)] animate-scale-in rounded-xl p-1.5 shadow-pop ring-1 ring-line focus:outline-none', className)}
          >
            {header && <div className="border-b border-line px-2.5 pb-2 pt-1.5">{header}</div>}
            {items.map((entry, i) => {
              if (entry.type === 'separator') {
                return <div key={`sep-${i}`} role="separator" className="my-1 h-px bg-line" />;
              }
              if (entry.type === 'label') {
                return (
                  <div key={`label-${i}`} className="px-2.5 pb-1 pt-2 text-2xs font-semibold uppercase tracking-wider text-ink-subtle">
                    {entry.label}
                  </div>
                );
              }
              const itemClass = cn(
                'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm outline-none transition-colors focus-visible:bg-sapphire-50 focus-visible:ring-0 focus-visible:ring-offset-0 dark:focus-visible:bg-sapphire-900/50',
                entry.danger ? 'text-danger-700 hover:bg-danger-50 focus-visible:bg-danger-50 dark:text-danger-500 dark:hover:bg-danger-700/20' : 'text-ink hover:bg-sunken',
                entry.disabled && 'cursor-not-allowed opacity-50',
              );
              const content = (
                <>
                  {entry.icon && <span className="text-ink-muted [&>svg]:h-4 [&>svg]:w-4">{entry.icon}</span>}
                  <span className="flex-1 truncate">{entry.label}</span>
                  {entry.checked && <span className="h-1.5 w-1.5 rounded-full bg-sapphire-600" aria-hidden="true" />}
                  {entry.shortcut && <kbd className="font-mono text-2xs text-ink-subtle">{entry.shortcut}</kbd>}
                </>
              );
              const role = entry.checked !== undefined ? 'menuitemradio' : 'menuitem';
              if (entry.href && !entry.disabled) {
                return (
                  <a key={entry.id ?? entry.label} role={role} aria-checked={entry.checked} tabIndex={-1} href={entry.href} className={itemClass} onClick={() => close(false)}>
                    {content}
                  </a>
                );
              }
              return (
                <button key={entry.id ?? entry.label} type="button" role={role} aria-checked={entry.checked} aria-disabled={entry.disabled || undefined} tabIndex={-1} className={itemClass} onClick={() => !entry.disabled && run(entry)}>
                  {content}
                </button>
              );
            })}
        </Floating>
      )}
    </>
  );
}
